import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
// 'deleteDoc' has been removed from this import as it was unused
import { collection, query, orderBy, doc, setDoc, writeBatch, where, getDocs, updateDoc } from 'firebase/firestore';
import Select from 'react-select';
import { Loader2, PlusCircle, Trash2, Edit, XCircle } from 'lucide-react';

export default function AdminMatrixPage() {
  const [nodes, setNodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [allCryptos, setAllCryptos] = useState([]);
  const [selectedCryptos, setSelectedCryptos] = useState([]);

  const [editingNode, setEditingNode] = useState(null);

  const [typeId, setTypeId] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [parentId, setParentId] = useState('');

  useEffect(() => {
    const fetchNodes = async () => {
      const q = query(collection(db, 'fraudMatrix'), orderBy('name'));
      const snapshot = await getDocs(q);
      setNodes(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setLoading(false);
    };
    fetchNodes();
  }, []);

  useEffect(() => {
    const fetchCryptos = async () => {
        try {
            const response = await fetch('https://api.coingecko.com/api/v3/coins/markets?vs_currency=usd&order=market_cap_desc&per_page=100&page=1&sparkline=false');
            const data = await response.json();
            const options = data.map(coin => ({ value: coin.id, label: `${coin.name} (${coin.symbol.toUpperCase()})`}));
            setAllCryptos(options);
        } catch (error) {
            console.error("Failed to fetch crypto list:", error);
        }
    };
    fetchCryptos();
  }, []);

  const handleEditClick = (node) => {
    setEditingNode(node);
    setTypeId(node.id);
    setName(node.name);
    setDescription(node.description);
    setParentId(node.parentId || '');
    setSelectedCryptos(node.cryptos || []);
  };

  const cancelEdit = () => {
    setEditingNode(null);
    resetForm();
  };
  
  const resetForm = () => {
    setTypeId('');
    setName('');
    setDescription('');
    setParentId('');
    setSelectedCryptos([]);
  };

  const handleDelete = async (nodeIdToDelete) => {
    if (window.confirm(`Are you sure you want to delete this item? This will also delete any child items.`)) {
      try {
        const batch = writeBatch(db);
        const childrenQuery = query(collection(db, 'fraudMatrix'), where('parentId', '==', nodeIdToDelete));
        const childrenSnapshot = await getDocs(childrenQuery);
        
        childrenSnapshot.forEach((doc) => {
          batch.delete(doc.ref);
        });

        const docRef = doc(db, 'fraudMatrix', nodeIdToDelete);
        batch.delete(docRef);
        await batch.commit();

      } catch (error) {
        console.error("Error deleting documents: ", error);
        alert("Failed to delete item(s).");
      }
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !description.trim()) {
      alert('Name and Description cannot be empty.');
      return;
    }
    
    if (!editingNode && !typeId.trim()) {
      alert('Type ID cannot be empty when creating a new item.');
      return;
    }

    setIsSubmitting(true);
    const dataToSave = {
        name,
        description,
        parentId: parentId || null,
        cryptos: selectedCryptos.length > 0 ? selectedCryptos : [{ value: 'other', label: 'Other' }]
    };

    try {
      if (editingNode) {
        const docRef = doc(db, 'fraudMatrix', editingNode.id);
        await updateDoc(docRef, dataToSave);
        cancelEdit();
      } else {
        const docRef = doc(db, 'fraudMatrix', typeId); 
        await setDoc(docRef, dataToSave);
        resetForm();
      }
    } catch (error) {
      console.error("Error writing document: ", error);
      alert("Failed to save matrix item.");
    }
    setIsSubmitting(false);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="animate-spin text-cyan-400" size={48} />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="glass-card rounded-2xl p-8">
        <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-bold text-white">
            {editingNode ? `Editing: ${editingNode.name}` : 'Add New Matrix Item'}
            </h2>
            {editingNode && (
            <button onClick={cancelEdit} className="flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors">
                <XCircle size={16} />
                Cancel Edit
            </button>
            )}
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Type ID</label>
            <input
              type="text"
              value={typeId}
              onChange={(e) => setTypeId(e.target.value)}
              placeholder="e.g., F001 or F001.1"
              className="w-full glass-card rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/50"
              required
              disabled={!!editingNode}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Parent Item (Optional)</label>
            <select
              value={parentId}
              onChange={(e) => setParentId(e.target.value)}
              className="w-full glass-card rounded-lg px-4 py-3 text-white bg-black/20 focus:outline-none focus:ring-2 focus:ring-cyan-500/50"
              disabled={!!editingNode && editingNode.id === parentId}
            >
              <option value="">-- No Parent (Top-Level Category) --</option>
              {nodes.map(node => (
                editingNode && editingNode.id === node.id ? null : (
                  <option key={node.id} value={node.id}>{node.name} ({node.id})</option>
                )
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Phishing"
              className="w-full glass-card rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/50"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows="3"
              placeholder="A short description of the fraud type..."
              className="w-full glass-card rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/50"
              required
            />
          </div>
          
          <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Associated Cryptocurrencies</label>
              <Select
                isMulti
                options={allCryptos}
                value={selectedCryptos}
                onChange={setSelectedCryptos}
                placeholder="Select cryptos (or leave for 'Other')"
                styles={{
                    control: (base) => ({ ...base, background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255, 255, 255, 0.1)' }),
                    menu: (base) => ({ ...base, background: '#1a202c' }),
                    option: (base, { isFocused }) => ({ ...base, background: isFocused ? 'rgba(255, 255, 255, 0.1)' : 'transparent' }),
                    multiValue: (base) => ({ ...base, background: 'rgba(56, 189, 248, 0.2)', color: '#e2e8f0' }),
                    multiValueLabel: (base) => ({...base, color: '#e2e8f0' }),
                }}
              />
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex items-center gap-2 bg-cyan-500/90 hover:bg-cyan-400 text-black font-bold py-2 px-4 rounded-lg text-sm transition-colors disabled:opacity-50"
            >
              {isSubmitting ? <Loader2 className="animate-spin" size={16} /> : <PlusCircle size={16} />}
              {isSubmitting ? 'Saving...' : (editingNode ? 'Save Changes' : 'Add Item')}
            </button>
          </div>
        </form>
      </div>
      
      <div className="glass-card rounded-2xl p-8">
        <h2 className="text-2xl font-bold text-white mb-4">Manage Existing Items</h2>
        <div className="space-y-2">
          {nodes.map(node => (
            <div key={node.id} className="glass-card p-3 rounded-lg flex justify-between items-center">
              <div>
                <p className="font-bold text-white">{node.name} <span className="text-xs text-gray-500 ml-2">({node.id})</span></p>
                <p className="text-sm text-gray-400">
                  Parent: {node.parentId ? nodes.find(p => p.id === node.parentId)?.name : 'None'}
                </p>
              </div>
              <div className="flex gap-2">
                <button onClick={() => handleEditClick(node)} className="p-2 hover:bg-white/10 rounded-lg transition-colors"><Edit size={16} className="text-cyan-400"/></button>
                <button onClick={() => handleDelete(node.id)} className="p-2 hover:bg-white/10 rounded-lg transition-colors"><Trash2 size={16} className="text-red-400"/></button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}